import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { SupplierRequest } from '@shop/types';
import { Plus } from 'lucide-react';
import { type SyntheticEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../components/layout';
import { Alert } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { type Columns, DataTable } from '../../components/ui/data-table';
import { Dialog } from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { Checkbox, Input } from '../../components/ui/input';
import { api, errorMessage } from '../../lib/api';
import { useCan } from '../../lib/session';
import type { SupplierRow } from '../../lib/types';

export const suppliersQuery = {
  queryKey: ['suppliers'],
  queryFn: () => api<{ items: SupplierRow[] }>('/suppliers'),
};

export function SupplierListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canWrite = useCan('suppliers.write');
  const suppliers = useQuery(suppliersQuery);
  const [open, setOpen] = useState(false);

  const columns = useMemo<Columns<SupplierRow>>(
    () => [
      { id: 'name', header: t('common.name'), accessorFn: (r) => r.supplier.name },
      { id: 'code', header: t('suppliers.code'), accessorFn: (r) => r.supplier.code },
      { id: 'country', header: t('suppliers.country'), accessorFn: (r) => r.supplier.countryCode },
      { id: 'priority', header: t('suppliers.priority'), accessorFn: (r) => r.supplier.priority },
      { id: 'offers', header: t('suppliers.offers'), accessorFn: (r) => r.offers },
      {
        id: 'unmatched',
        header: t('suppliers.unmatched'),
        accessorFn: (r) => r.unmatched,
        cell: ({ row }) =>
          row.original.unmatched > 0 ? (
            <Badge variant="warning">{row.original.unmatched}</Badge>
          ) : (
            '0'
          ),
      },
      {
        id: 'dropship',
        header: t('suppliers.dropship'),
        accessorFn: (r) => r.supplier.dropship,
        cell: ({ row }) => (row.original.supplier.dropship ? t('common.yes') : t('common.no')),
      },
    ],
    [t],
  );

  return (
    <>
      <PageHeader
        title={t('suppliers.title')}
        actions={
          canWrite ? (
            <Button
              onClick={() => {
                setOpen(true);
              }}
            >
              <Plus />
              {t('suppliers.new')}
            </Button>
          ) : null
        }
      />
      {suppliers.error ? (
        <Alert variant="destructive">{errorMessage(suppliers.error)}</Alert>
      ) : null}
      <DataTable
        columns={columns}
        data={suppliers.data?.items ?? []}
        loading={suppliers.isPending}
        rowKey={(r) => r.supplier.id}
        onRowClick={(r) => {
          void navigate({ to: '/suppliers/$supplierId', params: { supplierId: r.supplier.id } });
        }}
      />
      {open ? (
        <SupplierDialog
          onClose={() => {
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

function SupplierDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    code: '',
    name: '',
    countryCode: 'LV',
    currency: 'EUR',
    priority: '0',
    defaultLeadTimeDays: '3',
    staleAfterMinutes: '1440',
    safetyBuffer: '0',
    dropship: false,
    manualOrders: false,
  });
  const [error, setError] = useState<string>();

  const submit = async (e: SyntheticEvent) => {
    e.preventDefault();
    const body: SupplierRequest = {
      code: form.code.trim(),
      name: form.name.trim(),
      countryCode: form.countryCode.trim().toUpperCase(),
      currency: form.currency.trim().toUpperCase(),
      priority: Number(form.priority),
      defaultLeadTimeDays: Number(form.defaultLeadTimeDays),
      staleAfterMinutes: Number(form.staleAfterMinutes),
      safetyBuffer: Number(form.safetyBuffer),
      dropship: form.dropship,
      manualOrders: form.manualOrders,
      neutralPackaging: false,
    };
    try {
      await api('/suppliers', { method: 'POST', body });
      await queryClient.invalidateQueries({ queryKey: suppliersQuery.queryKey });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const text = (
    key: Exclude<keyof typeof form, 'dropship' | 'manualOrders'>,
    label: string,
    numeric = false,
  ) => (
    <Field label={label}>
      {(id) => (
        <Input
          id={id}
          required
          type={numeric ? 'number' : 'text'}
          min={numeric ? 0 : undefined}
          value={form[key]}
          onChange={(e) => {
            setForm({ ...form, [key]: e.target.value });
          }}
        />
      )}
    </Field>
  );

  return (
    <Dialog open onClose={onClose} title={t('suppliers.new')} className="max-w-2xl">
      <form onSubmit={(e) => void submit(e)} className="grid gap-4">
        <div className="grid gap-3 sm:grid-cols-2">
          {text('name', t('common.name'))}
          {text('code', t('suppliers.code'))}
          {text('countryCode', t('suppliers.country'))}
          {text('currency', t('suppliers.currency'))}
          {text('priority', t('suppliers.priority'), true)}
          {text('defaultLeadTimeDays', t('suppliers.leadTime'), true)}
          {text('staleAfterMinutes', t('suppliers.staleAfter'), true)}
          {text('safetyBuffer', t('suppliers.buffer'), true)}
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={form.dropship}
            onChange={(e) => {
              setForm({ ...form, dropship: e.target.checked });
            }}
          />
          {t('suppliers.dropship')}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={form.manualOrders}
            onChange={(e) => {
              setForm({ ...form, manualOrders: e.target.checked });
            }}
          />
          {t('suppliers.manualOrders')}
        </label>
        {error ? <Alert variant="destructive">{error}</Alert> : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit">{t('common.create')}</Button>
        </div>
      </form>
    </Dialog>
  );
}
